import { useState, useEffect } from "react";
import { useAtom } from "jotai";
import axiosNoAuthInstance from "../../axiosApiNoAuth";
import swal from "sweetalert";
import { Bar, BarChart, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Box, CircularProgress } from "@mui/material";
import {
  establishmentsVsEmploymentDataAtom,
  firmSizeRangeAtom,
  selectedFiltersAtom,
  selectedPlaceAtom,
  selectedSpatialLayerAtom,
  selectedSpatialValueAtom,
  spatialParameterAtom,
} from "../../../shared/state/atoms";

// Wage distribution colour swatch: green (bottom) → brown (middle) → orange (top)
// Anchored on the 3 wage distribution line colours: #009640, #84746C, #F0A400
const SEGMENT_COLORS = ['#009640', '#66bb6a', '#84746C', '#F5C242', '#F0A400'];

const SegmentLabel = (props) => {
  const { x, y, width, height, value } = props;
  if (!value || height < 14) return null;
  return (
    <text x={x + width / 2} y={y + height / 2} fill="#fff" textAnchor="middle" dominantBaseline="middle" fontSize={height < 20 ? 10 : 12} fontWeight="bold">
      {value.toFixed(1)}%
    </text>
  );
};

function EstablishmentsVsEmployment() {
  const [data, setData] = useAtom(establishmentsVsEmploymentDataAtom);
  const [selectedPlace] = useAtom(selectedPlaceAtom);
  const [selectedSpatialValue] = useAtom(selectedSpatialValueAtom);
  const [spatialParameter] = useAtom(spatialParameterAtom);
  const [selectedSpatialLayer] = useAtom(selectedSpatialLayerAtom);
  const [selectedFilters] = useAtom(selectedFiltersAtom);
  const [firmSizeRange] = useAtom(firmSizeRangeAtom);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  // Additional data for province and national charts
  const [provinceData, setProvinceData] = useState(null);
  const [nationalData, setNationalData] = useState(null);

  const handles = firmSizeRange?.establishmentsVsEmployment?.handles ?? [10, 50, 250, 1000];
  const handleParams = `&handle1=${handles[0]}&handle2=${handles[1]}&handle3=${handles[2]}&handle4=${handles[3]}`;

  const isProvinceSpatial = selectedSpatialLayer?.id === "province";

  // Determine province code for the province chart
  const provinceCode = isProvinceSpatial
    ? selectedSpatialValue // already the province code
    : selectedPlace?.province; // municipality or district — get parent province

  // Get display names for subtitles
  const placeDisplayName = selectedPlace?.name || selectedSpatialValue;
  // Province display name: municipality/district have province_n; province spatial has name
  const provinceDisplayName = isProvinceSpatial
    ? selectedPlace?.name
    : selectedPlace?.province_n || provinceCode;

  const roundAndNormalize = (items) => {
    const result = {};
    const rounded = items.map((item) => ({
      segment: item.segment,
      value: Math.round(item.percentage * 10) / 10,
    }));
    const sum = rounded.reduce((s, r) => s + r.value, 0);
    const diff = Math.round((100 - sum) * 10) / 10;
    if (diff !== 0 && rounded.length > 0) {
      rounded[rounded.length - 1].value = Math.round((rounded[rounded.length - 1].value + diff) * 10) / 10;
    }
    rounded.forEach((r) => { result[r.segment] = r.value; });
    return result;
  };

  const transformResponse = (responseData) => {
    if (!responseData) return null;
    const estData = roundAndNormalize(responseData.establishments);
    const empData = roundAndNormalize(responseData.employment);
    return {
      chartData: [
        { name: 'Establishments', ...estData },
        { name: 'Employment', ...empData },
      ],
      segments: responseData.establishments.map((item) => ({
        segment: item.segment,
        label: item.label,
      })),
    };
  };

  useEffect(() => {
    async function getData() {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const baseUrl = "/api/dashboard-establishments-vs-employment/";
        const yearParam = `&year=${selectedFilters?.singleYear}`;

        // Build parallel API calls
        const calls = [];

        // 1. Place chart (always)
        if (spatialParameter && spatialParameter !== "error" && selectedSpatialValue) {
          calls.push(
            axiosNoAuthInstance.get(`${baseUrl}?${spatialParameter}=${selectedSpatialValue}${yearParam}${handleParams}`)
              .then((r) => ({ key: "place", data: r.data }))
              .catch(() => ({ key: "place", data: null }))
          );
        }

        // 2. Province chart (skip if already at province level — province IS the place chart)
        if (!isProvinceSpatial && provinceCode) {
          calls.push(
            axiosNoAuthInstance.get(`${baseUrl}?province=${provinceCode}${yearParam}${handleParams}`)
              .then((r) => ({ key: "province", data: r.data }))
              .catch(() => ({ key: "province", data: null }))
          );
        }

        // 3. National chart (always)
        calls.push(
          axiosNoAuthInstance.get(`${baseUrl}?national=1${yearParam}${handleParams}`)
            .then((r) => ({ key: "national", data: r.data }))
            .catch(() => ({ key: "national", data: null }))
        );

        const results = await Promise.all(calls);

        let hasAnyData = false;
        for (const { key, data: responseData } of results) {
          if (key === "place" && responseData) {
            setData({ ...transformResponse(responseData), raw: responseData });
            hasAnyData = true;
          } else if (key === "province" && responseData) {
            setProvinceData(transformResponse(responseData));
            hasAnyData = true;
          } else if (key === "national" && responseData) {
            setNationalData(transformResponse(responseData));
            hasAnyData = true;
          }
        }

        if (!hasAnyData) {
          setData([]);
          setProvinceData(null);
          setNationalData(null);
          setErrorMessage(`No Data available for ${selectedFilters?.singleYear}`);
        }
        setIsLoading(false);
      } catch (error) {
        if (error?.response?.status === 404 || error?.response == null) {
          setIsLoading(false);
          setErrorMessage("No Data");
        } else {
          swal("Failed", `${error}`, "error", { timer: 4000, buttons: false });
          setIsLoading(false);
        }
      }
    }

    if (spatialParameter && selectedSpatialValue) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spatialParameter, selectedSpatialValue, selectedFilters?.singleYear, handles[0], handles[1], handles[2], handles[3]]);

  const segments = data?.segments || [];
  const segmentNames = segments.map((s) => s.segment);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const reversed = [...payload].reverse();
      return (
        <div className="p-2 bg-white border">
          <p className="m-0 font-bold">{payload[0]?.payload?.name}</p>
          {reversed.map((entry, i) => (
            <p key={i} className="m-0" style={{ color: SEGMENT_COLORS[reversed.length - 1 - i] }}>
              {entry.name}: {entry.value.toFixed(1)}%
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Render a single chart panel. isFirst=true renders the Y-axis; others hide it.
  const renderChart = (chartData, subtitle, isFirst) => {
    if (!chartData?.chartData) return null;
    return (
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="text-center text-sm font-semibold mb-2 text-gray-700" style={{ paddingLeft: 40 }}>{subtitle}</div>
        <ResponsiveContainer width="100%" height={420}>
          <BarChart
            data={chartData.chartData}
            margin={{ top: 10, right: 15, left: 5, bottom: 20 }}
          >
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis
              domain={[0, 100]}
              ticks={[0, 20, 40, 60, 80, 100]}
              tick={isFirst ? { fontSize: 11 } : false}
              label={isFirst ? { value: "% of Total", angle: -90, dx: -25, fontSize: 11 } : false}
              axisLine={isFirst}
              tickLine={isFirst}
              width={isFirst ? 60 : 60}
            />
            <Tooltip content={<CustomTooltip />} />
            {segmentNames.map((seg, i) => (
              <Bar key={seg} dataKey={seg} stackId="a" fill={SEGMENT_COLORS[i]}>
                <LabelList dataKey={seg} content={SegmentLabel} />
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Determine how many charts to show and their widths
  const charts = [];
  if (!isProvinceSpatial && data?.chartData) {
    charts.push({ data, subtitle: placeDisplayName });
  }
  if (isProvinceSpatial && data?.chartData) {
    // Province spatial: the place chart IS the province chart
    charts.push({ data, subtitle: provinceDisplayName });
  }
  if (!isProvinceSpatial && provinceData) {
    charts.push({ data: provinceData, subtitle: provinceDisplayName });
  }
  if (nationalData) {
    charts.push({ data: nationalData, subtitle: "South Africa" });
  }
  // chartWidth no longer needed — using flex: 1 for even spacing

  return (
    <div className="flex">
      <div className="w-full">
        <div className="text-lg px-10 pt-5 font-sans font-bold">
          {selectedSpatialLayer?.name} / {selectedPlace?.name} / <span style={{ color: "#1976d2" }}>Small Business</span>
        </div>
        <div className="text-3xl font-bold px-10 mb-10 font-roboto">
          Establishments & Jobs {selectedFilters?.singleYear || ""}
        </div>
        {!errorMessage ? (
          <div>
            {isLoading && (
              <div className="absolute left-0 top-0 w-full h-full opacity-30 bg-ccre-gray-900 z-20">
                <Box sx={{ textAlign: "center", position: "relative", top: "40%" }}>
                  <CircularProgress color="success" size="3rem" />
                </Box>
              </div>
            )}
            {charts.length > 0 && (
              <>
                <div className="flex px-6 gap-0">
                  {charts.map((chart, i) => renderChart(chart.data, chart.subtitle, i === 0))}
                </div>
                <div className="ml-16 mt-3 mb-8 flex flex-wrap gap-6">
                  {segments.map((seg, i) => (
                    <div className="flex items-center" key={seg.segment}>
                      <div className="w-4 h-4 mr-2" style={{ backgroundColor: SEGMENT_COLORS[i] }} />
                      <span>{seg.segment}: {seg.label}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="p-20 flex justify-center">{errorMessage}</div>
        )}
      </div>
    </div>
  );
}

export default EstablishmentsVsEmployment;
